// Schnellerfassung — Probetraining per Sprach- oder Textnotiz (14.09.2026).
// Mikrofon = Web Speech API des Browsers (Chrome, Safari, Edge), Text als
// Fallback. Die KI zieht die Felder heraus, der Kern schlaegt freie Termine
// vor, gebucht wird ueber die zentrale Buchungsfunktion. Jede Angabe ist vor
// dem Buchen noch editierbar — der Mensch bestaetigt, die KI schlaegt vor.
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { schnellerfassungAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Mic, MicOff, Sparkles, CalendarCheck, UserPlus, Loader2, RotateCcw, AlertTriangle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BEISPIEL = 'z. B. „Leon Muster, 0176 1234567, Sohn 8 Jahre, will Donnerstag um 17 Uhr zum Probetraining, Mutter hat angerufen"';
const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export default function SchnellerfassungPage() {
  const [text, setText] = useState('');
  const [hoert, setHoert] = useState(false);
  const [zwischen, setZwischen] = useState('');
  const [laden, setLaden] = useState(false);
  const [erg, setErg] = useState(null);           // Antwort von /verstehen
  const [f, setF] = useState(null);               // editierbare Felder
  const [terminId, setTerminId] = useState('');
  const [slots, setSlots] = useState([]);
  const [bucht, setBucht] = useState(false);
  const [fertig, setFertig] = useState(null);
  const rec = useRef(null);

  // ── Spracherkennung ─────────────────────────────────────────────────────
  const stopp = useCallback(() => { try { rec.current?.stop(); } catch { /* egal */ } setHoert(false); setZwischen(''); }, []);
  const start = () => {
    if (!SR) { toast.error('Dieser Browser kann keine Spracherkennung. Bitte Chrome, Edge oder Safari nutzen oder tippen.'); return; }
    const r = new SR();
    r.lang = 'de-DE'; r.interimResults = true; r.continuous = true; r.maxAlternatives = 1;
    let endgueltig = '';
    r.onresult = (e) => {
      let zw = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) endgueltig += (endgueltig ? ' ' : '') + t.trim(); else zw += t;
      }
      setZwischen(zw);
      if (endgueltig) setText((alt) => (alt ? alt.replace(/\s+$/, '') + ' ' : '') + endgueltig.trim()), endgueltig = '';
    };
    r.onerror = (e) => { if (e.error !== 'no-speech') toast.error(`Mikrofon: ${e.error}`); stopp(); };
    r.onend = () => { setHoert(false); setZwischen(''); };
    rec.current = r;
    try { r.start(); setHoert(true); } catch { toast.error('Mikrofon konnte nicht gestartet werden.'); }
  };
  useEffect(() => () => stopp(), [stopp]);

  // ── Verstehen ───────────────────────────────────────────────────────────
  const verstehen = async () => {
    if (text.trim().length < 3) return;
    stopp(); setLaden(true); setFertig(null);
    try {
      const r = await schnellerfassungAPI.verstehen(text.trim());
      setErg(r.data);
      const fe = r.data.felder;
      setF({ vorname: fe.vorname || '', nachname: fe.nachname || '', telefon: fe.telefon || '', email: fe.email || '',
             zielgruppe: fe.zielgruppe || 'unbekannt', kind_alter: fe.kind_alter || '', kurs_slug: r.data.kurs_slugs?.[0] || '',
             notiz: fe.notiz || '', wunsch_datum: fe.wunsch_datum || '', wunsch_uhrzeit: fe.wunsch_uhrzeit || '', wunsch_tagesteil: fe.wunsch_tagesteil || '' });
      setSlots(r.data.slots || []);
      setTerminId(r.data.slots?.find((s) => s.empfohlen)?.termin_id || r.data.slots?.[0]?.termin_id || '');
    } catch (e) { toast.error(e.response?.data?.detail || 'Konnte die Notiz nicht auswerten.'); }
    setLaden(false);
  };

  const kursWechseln = async (slug) => {
    setF((p) => ({ ...p, kurs_slug: slug })); setTerminId('');
    if (!slug) { setSlots([]); return; }
    try {
      const r = await schnellerfassungAPI.slots({ kurs_slug: slug, wunsch_datum: f?.wunsch_datum || undefined, wunsch_uhrzeit: f?.wunsch_uhrzeit || undefined, wunsch_tagesteil: f?.wunsch_tagesteil || undefined });
      setSlots(r.data || []); setTerminId(r.data?.[0]?.termin_id || '');
    } catch { toast.error('Termine konnten nicht geladen werden.'); }
  };

  // ── Buchen ──────────────────────────────────────────────────────────────
  const buchen = async (mitTermin) => {
    if (!f?.telefon) { toast.error('Ohne Handynummer geht es nicht.'); return; }
    if (mitTermin && !terminId) { toast.error('Bitte einen Termin wählen.'); return; }
    setBucht(true);
    try {
      const r = await schnellerfassungAPI.buchen({
        text: text.trim(), vorname: f.vorname, nachname: f.nachname, telefon: f.telefon, email: f.email || null,
        zielgruppe: f.zielgruppe, kind_alter: f.zielgruppe === 'kind' && f.kind_alter ? Number(f.kind_alter) : null,
        kurs_slug: f.kurs_slug || null, termin_id: mitTermin ? terminId : null, notiz: f.notiz,
      });
      setFertig(r.data);
      toast.success(mitTermin ? `Gebucht: ${r.data.termin?.label}` : 'Lead gespeichert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Buchung fehlgeschlagen.'); }
    setBucht(false);
  };

  const neu = () => { setText(''); setErg(null); setF(null); setSlots([]); setTerminId(''); setFertig(null); };
  const name = f ? [f.vorname, f.nachname].filter(Boolean).join(' ') : '';

  return (
    <div className="space-y-5 max-w-3xl" data-testid="schnellerfassung">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Mic className="h-6 w-6 text-marke" /> Schnellerfassung</h1>
        <p className="text-sm text-muted-foreground mt-1">Einmal kurz sprechen oder tippen: Wer, Handynummer, Alter, Wunschtermin. Die KI füllt die Felder, du bestätigst, der Termin ist gebucht.</p>
      </div>

      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex gap-3 items-stretch">
            <button type="button" onClick={hoert ? stopp : start} data-testid="mic"
                    className={`shrink-0 w-24 rounded-xl flex flex-col items-center justify-center gap-1 border text-xs font-medium transition ${hoert ? 'bg-red-600 text-white border-red-600 animate-pulse' : 'bg-muted/40 hover:bg-muted border-border'}`}>
              {hoert ? <MicOff className="h-7 w-7" /> : <Mic className="h-7 w-7" />}
              {hoert ? 'Stopp' : 'Sprechen'}
            </button>
            <Textarea value={text + (zwischen ? ' ' + zwischen : '')} onChange={(e) => setText(e.target.value)} rows={4}
                      placeholder={BEISPIEL} className="text-base" data-testid="notiz" />
          </div>
          {!SR && <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Spracherkennung gibt es in diesem Browser nicht, tippen geht immer.</p>}
          <div className="flex flex-wrap gap-2 justify-end">
            {(erg || text) && <Button variant="ghost" size="sm" onClick={neu}><RotateCcw className="h-4 w-4 mr-1.5" /> Neu</Button>}
            <Button onClick={verstehen} disabled={laden || text.trim().length < 3} data-testid="verstehen">
              {laden ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Auswerten
            </Button>
          </div>
        </CardContent>
      </Card>

      {fertig && (
        <Card className="border-emerald-500/50" data-testid="fertig">
          <CardContent className="pt-5 space-y-2">
            <p className="font-semibold text-emerald-600 flex items-center gap-2"><CalendarCheck className="h-5 w-5" /> {fertig.termin ? 'Probetraining gebucht' : 'Lead gespeichert'}</p>
            {fertig.termin && <p className="text-sm">{name} · <b>{fertig.termin.label}</b></p>}
            <p className="text-xs text-muted-foreground">{fertig.termin ? 'Bestätigung läuft über die übliche Kette (Freigabe-Zentrale bzw. Aufgabe).' : 'Nächster Schritt am Lead: Probetraining-Termin vereinbaren.'}</p>
            <div className="flex gap-2 pt-1">
              {fertig.lead_id && <Button size="sm" variant="outline" asChild><Link to={`/leads/${fertig.lead_id}`}><ExternalLink className="h-4 w-4 mr-1.5" /> Lead öffnen</Link></Button>}
              <Button size="sm" onClick={neu}><Mic className="h-4 w-4 mr-1.5" /> Nächste Erfassung</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {erg && f && !fertig && (
        <Card data-testid="vorschlag">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex flex-wrap items-center gap-2">
              Verstanden
              {erg.lead ? <Badge variant="secondary">Bestehender Lead: {erg.lead.name}</Badge> : <Badge variant="outline">Neuer Kontakt</Badge>}
              {erg.lead && <Link to={`/leads/${erg.lead.id}`} className="text-xs underline text-muted-foreground">öffnen</Link>}
            </CardTitle>
            {f.notiz === '' && erg.felder.rueckfragen.length === 0 ? null : (
              erg.felder.rueckfragen.length > 0 && (
                <div className="text-xs text-amber-600 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /><span>Bitte prüfen: {erg.felder.rueckfragen.join(' · ')}</span></div>
              )
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Vorname</Label><Input value={f.vorname} onChange={(e) => setF({ ...f, vorname: e.target.value })} data-testid="f-vorname" /></div>
              <div><Label className="text-xs">Nachname</Label><Input value={f.nachname} onChange={(e) => setF({ ...f, nachname: e.target.value })} /></div>
              <div><Label className="text-xs">Handynummer</Label><Input value={f.telefon} onChange={(e) => setF({ ...f, telefon: e.target.value })} className={!f.telefon ? 'border-amber-500' : ''} data-testid="f-telefon" /></div>
              <div><Label className="text-xs">E-Mail (optional)</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><Label className="text-xs">Wer trainiert</Label>
                <select value={f.zielgruppe} onChange={(e) => setF({ ...f, zielgruppe: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="erwachsen">Erwachsene Person</option><option value="kind">Kind / Jugendliche</option><option value="unbekannt">Unklar</option>
                </select></div>
              <div><Label className="text-xs">Alter (bei Kindern)</Label><Input type="number" min={1} max={17} value={f.kind_alter} onChange={(e) => setF({ ...f, kind_alter: e.target.value })} disabled={f.zielgruppe !== 'kind'} /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Kurs</Label>
                <select value={f.kurs_slug} onChange={(e) => kursWechseln(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="f-kurs">
                  <option value="">— Kurs wählen —</option>
                  {(erg.kurse || []).map((k) => <option key={k.slug} value={k.slug}>{k.name}{erg.kurs_slugs?.includes(k.slug) ? ' · passend' : ''}</option>)}
                </select></div>
              <div className="sm:col-span-2"><Label className="text-xs">Notiz</Label><Input value={f.notiz} onChange={(e) => setF({ ...f, notiz: e.target.value })} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs">Termin{f.wunsch_datum || f.wunsch_uhrzeit || f.wunsch_tagesteil ? ` · Wunsch: ${[f.wunsch_datum, f.wunsch_uhrzeit, f.wunsch_tagesteil].filter(Boolean).join(' ')}` : ''}</Label>
                <span className="text-[11px] text-muted-foreground">Heute: {erg.kontext?.wochentag}, {erg.kontext?.heute}</span>
              </div>
              {slots.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">Keine freien Termine für diesen Kurs in den nächsten drei Wochen. Kurs wechseln oder nur den Lead speichern.</p>
              ) : (
                <div className="space-y-1.5" data-testid="slots">
                  {slots.map((s) => (
                    <label key={s.termin_id} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer text-sm ${terminId === s.termin_id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                      <input type="radio" name="termin" checked={terminId === s.termin_id} onChange={() => setTerminId(s.termin_id)} />
                      <span className="flex-1">{s.label}</span>
                      {s.empfohlen && <Badge variant="secondary" className="text-[10px]">passt am besten</Badge>}
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{s.restplaetze} frei</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 justify-end pt-1">
              <Button variant="outline" onClick={() => buchen(false)} disabled={bucht} data-testid="nur-lead"><UserPlus className="h-4 w-4 mr-1.5" /> Nur Lead speichern</Button>
              <Button onClick={() => buchen(true)} disabled={bucht || !terminId} data-testid="buchen">
                {bucht ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarCheck className="h-4 w-4 mr-1.5" />} Probetraining buchen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
